import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'
import { cashUpBreakdownFromRecord } from '../services/cashUpService'

export class PDFGenerator {
  static normalizeBusinessProfile(businessProfile, branch = null) {
    const p = businessProfile || {}
    const b = branch || {}
    return {
      tradingName: p.tradingName || p.name || 'TrendSetter',
      legalName: p.legalName || '',
      registrationNumber: p.registrationNumber || '',
      vatNumber: p.vatNumber || '',
      // Prefer the branch address for location-specific slips
      address: b.address || p.address || '',
      phone: b.phone || p.phone || '',
      email: b.email || p.email || '',
      receiptFooter: p.receiptFooter || '',
      branchName: b.name || '',
    }
  }

  /**
   * ThinkinCode attribution lines for slips / invoices (no emoji — jsPDF Helvetica).
   * Returns the Y after the last line.
   */
  static drawThinkinCodeFooter(pdf, { pageWidth, y, lineHeight = 4, fontSize = 7 }) {
    pdf.setFont('helvetica', 'normal')
    pdf.setFontSize(fontSize)
    pdf.setTextColor(0, 0, 0)
    pdf.text('For more info visit www.thinkincode.co.za', pageWidth / 2, y, { align: 'center' })
    y += lineHeight
    pdf.text('Powered by thinkinCode', pageWidth / 2, y, { align: 'center' })
    return y + lineHeight
  }
  
  /**
   * Generate thermal receipt PDF (80mm width)
   */
  static async generateThermalReceipt(invoiceData, businessProfile, branch = null) {
    try {
      const biz = this.normalizeBusinessProfile(businessProfile, branch)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, 200] // 80mm width thermal paper
      })

      // Set font
      pdf.setFont('helvetica')
      
      let yPosition = 10
      const lineHeight = 4
      const pageWidth = 80
      const margin = 5

      // Header
      pdf.setFontSize(12)
      pdf.setFont('helvetica', 'bold')
      pdf.text(biz.tradingName, pageWidth / 2, yPosition, { align: 'center' })
      yPosition += lineHeight * 1.5

      pdf.setFontSize(8)
      pdf.setFont('helvetica', 'normal')
      if (biz.address) {
        const lines = pdf.splitTextToSize(biz.address, pageWidth - margin * 2)
        lines.forEach((line) => {
          pdf.text(line, pageWidth / 2, yPosition, { align: 'center' })
          yPosition += lineHeight
        })
      }
      if (biz.phone) {
        pdf.text(`Tel: ${biz.phone}`, pageWidth / 2, yPosition, { align: 'center' })
        yPosition += lineHeight
      }
      if (biz.email) {
        pdf.text(biz.email, pageWidth / 2, yPosition, { align: 'center' })
        yPosition += lineHeight
      }
      if (biz.vatNumber) {
        pdf.text(`VAT: ${biz.vatNumber}`, pageWidth / 2, yPosition, { align: 'center' })
        yPosition += lineHeight
      }
      yPosition += lineHeight

      // Dashed line
      pdf.setLineDashPattern([1, 1], 0)
      pdf.line(margin, yPosition, pageWidth - margin, yPosition)
      yPosition += lineHeight

      // Invoice details
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(9)
      pdf.text(`Invoice: ${invoiceData.id}`, margin, yPosition)
      yPosition += lineHeight

      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(8)
      const issueDate = new Date(invoiceData.issueDate).toLocaleDateString('en-ZA')
      pdf.text(`Date: ${issueDate}`, margin, yPosition)
      yPosition += lineHeight
      pdf.text(`Customer: ${invoiceData.customerName}`, margin, yPosition)
      yPosition += lineHeight
      const customerCode = invoiceData.customerCode || invoiceData.metadata?.customerCode
      if (customerCode) {
        pdf.text(`Code: ${customerCode}`, margin, yPosition)
        yPosition += lineHeight
      }
      yPosition += lineHeight * 0.5

      // Customer balances (vouchers/subscription)
      const customerSnapshot = invoiceData?.metadata?.customerSnapshot
      const subscriptionSnapshot = invoiceData?.metadata?.subscriptionSnapshot
      if (customerSnapshot || subscriptionSnapshot) {
        pdf.setLineDashPattern([1, 1], 0)
        pdf.line(margin, yPosition, pageWidth - margin, yPosition)
        yPosition += lineHeight

        pdf.setFont('helvetica', 'bold')
        pdf.text('BALANCES:', margin, yPosition)
        yPosition += lineHeight

        pdf.setFont('helvetica', 'normal')
        if (customerSnapshot && typeof customerSnapshot.voucherBalance === 'number') {
          pdf.text(`Voucher balance: ${customerSnapshot.voucherBalance}`, margin, yPosition)
          yPosition += lineHeight
        }

        if (subscriptionSnapshot) {
          const plan = subscriptionSnapshot.planType ? ` (${subscriptionSnapshot.planType})` : ''
          const remaining =
            subscriptionSnapshot.remaining === 'unlimited'
              ? 'Unlimited'
              : typeof subscriptionSnapshot.remaining === 'number'
                ? `${subscriptionSnapshot.remaining}`
                : 'N/A'

          pdf.text(`Subscription${plan}: ${remaining}`, margin, yPosition)
          yPosition += lineHeight

          if (subscriptionSnapshot.expiryDate) {
            const exp = new Date(subscriptionSnapshot.expiryDate).toLocaleDateString('en-ZA')
            pdf.setFontSize(7)
            pdf.text(`Expires: ${exp}`, margin, yPosition)
            pdf.setFontSize(8)
            yPosition += lineHeight
          }
        }

        yPosition += lineHeight * 0.5
      }

      // Items
      pdf.setFont('helvetica', 'bold')
      pdf.text('ITEMS:', margin, yPosition)
      yPosition += lineHeight

      pdf.setFont('helvetica', 'normal')
      invoiceData.items.forEach(item => {
        const itemText = `${item.description}`
        const priceText = `R${item.total.toFixed(2)}`
        
        pdf.text(itemText, margin, yPosition)
        pdf.text(priceText, pageWidth - margin, yPosition, { align: 'right' })
        yPosition += lineHeight
        
        if (item.quantity > 1) {
          pdf.setFontSize(7)
          pdf.text(`  ${item.quantity} x R${item.unitPrice.toFixed(2)}`, margin + 2, yPosition)
          yPosition += lineHeight
          pdf.setFontSize(8)
        }
      })

      yPosition += lineHeight
      pdf.setLineDashPattern([1, 1], 0)
      pdf.line(margin, yPosition, pageWidth - margin, yPosition)
      yPosition += lineHeight

      // Totals
      pdf.text('Subtotal:', margin, yPosition)
      pdf.text(`R${invoiceData.subtotal.toFixed(2)}`, pageWidth - margin, yPosition, { align: 'right' })
      yPosition += lineHeight

      if (invoiceData.discount.amount > 0) {
        pdf.text(`Discount (${invoiceData.discount.percentage}%):`, margin, yPosition)
        pdf.text(`-R${invoiceData.discount.amount.toFixed(2)}`, pageWidth - margin, yPosition, { align: 'right' })
        yPosition += lineHeight
      }

      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(10)
      pdf.text('TOTAL:', margin, yPosition)
      pdf.text(`R${invoiceData.total.toFixed(2)}`, pageWidth - margin, yPosition, { align: 'right' })
      yPosition += lineHeight * 2

      // Payment method
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(8)
      pdf.text(`Payment: ${invoiceData.paymentMethod.toUpperCase()}`, margin, yPosition)
      yPosition += lineHeight * 2

      // Footer
      pdf.setLineDashPattern([1, 1], 0)
      pdf.line(margin, yPosition, pageWidth - margin, yPosition)
      yPosition += lineHeight

      pdf.text('Thank you for your business!', pageWidth / 2, yPosition, { align: 'center' })
      yPosition += lineHeight
      if (biz.receiptFooter) {
        const lines = pdf.splitTextToSize(biz.receiptFooter, pageWidth - margin * 2)
        lines.forEach((line) => {
          pdf.text(line, pageWidth / 2, yPosition, { align: 'center' })
          yPosition += lineHeight
        })
      } else {
        pdf.text('Please come again', pageWidth / 2, yPosition, { align: 'center' })
        yPosition += lineHeight
      }

      yPosition += lineHeight * 0.5
      this.drawThinkinCodeFooter(pdf, { pageWidth, y: yPosition, lineHeight, fontSize: 6 })

      return pdf.output('blob')
    } catch (error) {
      console.error('Error generating thermal receipt:', error)
      throw new Error('Failed to generate thermal receipt')
    }
  }

  /**
   * Generate A4 invoice PDF (plain black & white)
   */
  static async generateA4Invoice(invoiceData, businessProfile, branch = null) {
    try {
      const biz = this.normalizeBusinessProfile(businessProfile, branch)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      })

      const pageWidth = 210
      const pageHeight = 297
      const margin = 20
      const leftColWidth = 100
      const lineGap = 5
      let yPosition = margin

      pdf.setTextColor(0, 0, 0)
      pdf.setDrawColor(0, 0, 0)
      pdf.setLineWidth(0.3)

      // Left: business details (stacked so lines never overlap)
      let leftY = yPosition
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(16)
      pdf.text(biz.tradingName, margin, leftY)
      leftY += 7

      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(10)
      if (biz.legalName) {
        pdf.text(biz.legalName, margin, leftY)
        leftY += lineGap
      }
      if (biz.branchName) {
        pdf.text(biz.branchName, margin, leftY)
        leftY += lineGap
      }
      if (biz.address) {
        const addrLines = pdf.splitTextToSize(biz.address, leftColWidth)
        addrLines.forEach((line) => {
          pdf.text(line, margin, leftY)
          leftY += lineGap
        })
      }
      if (biz.phone) {
        pdf.text(`Tel: ${biz.phone}`, margin, leftY)
        leftY += lineGap
      }
      if (biz.email) {
        const emailLines = pdf.splitTextToSize(`Email: ${biz.email}`, leftColWidth)
        emailLines.forEach((line) => {
          pdf.text(line, margin, leftY)
          leftY += lineGap
        })
      }

      // Right: invoice meta
      let rightY = yPosition
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(16)
      pdf.text('INVOICE', pageWidth - margin, rightY, { align: 'right' })
      rightY += 8

      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(10)
      const issueDate = new Date(invoiceData.issueDate).toLocaleDateString('en-ZA')
      pdf.text(`Invoice #: ${invoiceData.id}`, pageWidth - margin, rightY, { align: 'right' })
      rightY += lineGap
      pdf.text(`Date: ${issueDate}`, pageWidth - margin, rightY, { align: 'right' })
      rightY += lineGap

      yPosition = Math.max(leftY, rightY) + 6
      pdf.line(margin, yPosition, pageWidth - margin, yPosition)
      yPosition += 8

      // Bill To
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(11)
      pdf.text('Bill To', margin, yPosition)
      yPosition += lineGap

      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(10)
      pdf.text(invoiceData.customerName || 'Walk-in Customer', margin, yPosition)
      yPosition += lineGap

      const customerCode = invoiceData.customerCode || invoiceData.metadata?.customerCode
      if (customerCode) {
        pdf.text(`Customer code: ${customerCode}`, margin, yPosition)
        yPosition += lineGap
      } else if (invoiceData.customerPhone) {
        pdf.text(`Phone: ${invoiceData.customerPhone}`, margin, yPosition)
        yPosition += lineGap
      }

      yPosition += 4
      pdf.line(margin, yPosition, pageWidth - margin, yPosition)
      yPosition += 6

      // Items table
      const tableStartY = yPosition
      const colWidths = [90, 20, 30, 30]
      const tableHeaders = ['Description', 'Qty', 'Unit Price', 'Total']
      const rowH = 7

      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(10)
      let xPosition = margin + 3
      tableHeaders.forEach((header, index) => {
        const align = index === 0 ? 'left' : 'right'
        const textX = index === 0 ? xPosition : xPosition + colWidths[index] - 3
        pdf.text(header, textX, yPosition + 5, { align })
        xPosition += colWidths[index]
      })
      yPosition += rowH
      pdf.line(margin, yPosition, pageWidth - margin, yPosition)

      pdf.setFont('helvetica', 'normal')
      ;(invoiceData.items || []).forEach((item) => {
        yPosition += rowH
        xPosition = margin + 3

        const desc = String(item.description || '')
        const descLines = pdf.splitTextToSize(desc, colWidths[0] - 4)
        pdf.text(descLines[0] || '', xPosition, yPosition)
        xPosition += colWidths[0]

        pdf.text(String(item.quantity ?? 0), xPosition + colWidths[1] - 3, yPosition, { align: 'right' })
        xPosition += colWidths[1]

        pdf.text(`R${Number(item.unitPrice || 0).toFixed(2)}`, xPosition + colWidths[2] - 3, yPosition, { align: 'right' })
        xPosition += colWidths[2]

        pdf.text(`R${Number(item.total || 0).toFixed(2)}`, xPosition + colWidths[3] - 3, yPosition, { align: 'right' })
      })

      yPosition += 3
      pdf.rect(margin, tableStartY, pageWidth - 2 * margin, yPosition - tableStartY)

      yPosition += 10

      // Totals
      const totalsX = pageWidth - margin - 55
      const discount = invoiceData.discount || { amount: 0, percentage: 0 }

      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(10)
      pdf.text('Subtotal:', totalsX, yPosition)
      pdf.text(`R${Number(invoiceData.subtotal || 0).toFixed(2)}`, pageWidth - margin, yPosition, { align: 'right' })
      yPosition += 6

      if (Number(discount.amount) > 0) {
        const pct = discount.percentage != null ? ` (${discount.percentage}%)` : ''
        pdf.text(`Discount${pct}:`, totalsX, yPosition)
        pdf.text(`-R${Number(discount.amount).toFixed(2)}`, pageWidth - margin, yPosition, { align: 'right' })
        yPosition += 6
      }

      pdf.line(totalsX, yPosition, pageWidth - margin, yPosition)
      yPosition += 7

      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(12)
      pdf.text('TOTAL:', totalsX, yPosition)
      pdf.text(`R${Number(invoiceData.total || 0).toFixed(2)}`, pageWidth - margin, yPosition, { align: 'right' })

      // Footer
      yPosition = pageHeight - 36
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(9)
      pdf.setTextColor(0, 0, 0)
      pdf.text(`Thank you for choosing ${biz.tradingName}.`, pageWidth / 2, yPosition, { align: 'center' })
      const complianceParts = []
      if (biz.vatNumber) complianceParts.push(`VAT: ${biz.vatNumber}`)
      if (biz.registrationNumber) complianceParts.push(`Reg: ${biz.registrationNumber}`)
      complianceParts.push('Payment Terms: Due on Receipt')
      pdf.text(complianceParts.join(' | '), pageWidth / 2, yPosition + 5, { align: 'center' })
      this.drawThinkinCodeFooter(pdf, {
        pageWidth,
        y: yPosition + 12,
        lineHeight: 4,
        fontSize: 8,
      })

      return pdf.output('blob')
    } catch (error) {
      console.error('Error generating A4 invoice:', error)
      throw new Error('Failed to generate A4 invoice')
    }
  }

  /**
   * Open a PDF blob in a print dialog (thermal or A4).
   */
  static async printPdf(blob, { filename = 'document.pdf', width = 300, height = 600 } = {}) {
    try {
      const url = URL.createObjectURL(blob)

      const printWindow = window.open(url, '_blank', `width=${width},height=${height}`)
      
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.focus()
          printWindow.print()

          // Clean up blob URL after printing (do not auto-close the window)
          // Some browsers won't reliably fire afterprint; keep a fallback timer.
          const cleanup = () => {
            try {
              URL.revokeObjectURL(url)
            } catch {
              // ignore
            }
          }

          printWindow.onafterprint = cleanup
          setTimeout(cleanup, 60_000)
        }
      } else {
        // Fallback: download the PDF
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }
    } catch (error) {
      console.error('Error printing PDF:', error)
      throw new Error('Failed to print')
    }
  }

  static async printThermalReceipt(blob) {
    return this.printPdf(blob, { filename: 'thermal-receipt.pdf', width: 300, height: 600 })
  }

  /**
   * Download PDF
   */
  static downloadPDF(blob, filename) {
    try {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading PDF:', error)
      throw new Error('Failed to download PDF')
    }
  }

  /**
   * Generate a cash-up slip PDF (A4 or 80mm thermal).
   */
  static async generateCashUpSlip(cashUpData, businessProfile, branch = null, options = {}) {
    try {
      const format = options.format === 'thermal' ? 'thermal' : 'a4'
      const isThermal = format === 'thermal'
      const biz = this.normalizeBusinessProfile(businessProfile, branch)
      const pageWidth = isThermal ? 80 : 210
      const pageHeight = 297
      const margin = isThermal ? 5 : 20
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: isThermal ? [pageWidth, pageHeight] : 'a4',
      })

      let y = margin
      const fmt = (n) => `R${Number(n || 0).toFixed(2)}`
      const labelX = margin
      const valueX = pageWidth - margin
      const contentWidth = pageWidth - margin * 2
      const titleSize = isThermal ? 11 : 20
      const headingSize = isThermal ? 9 : 16
      const bodySize = isThermal ? 8 : 11
      const lineGap = isThermal ? 4 : 6
      const pageBottom = pageHeight - margin

      const ensureSpace = (need = 8) => {
        if (y + need > pageBottom) {
          pdf.addPage(isThermal ? [pageWidth, pageHeight] : 'a4', 'portrait')
          y = margin
        }
      }

      const dashedRule = () => {
        ensureSpace(6)
        if (isThermal) pdf.setLineDashPattern([1, 1], 0)
        pdf.setDrawColor(isThermal ? 0 : 226, isThermal ? 0 : 232, isThermal ? 0 : 240)
        pdf.line(margin, y, pageWidth - margin, y)
        if (isThermal) pdf.setLineDashPattern([], 0)
        y += isThermal ? 4 : 10
      }

      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(titleSize)
      pdf.setTextColor(0, 0, 0)
      if (isThermal) {
        pdf.text(biz.tradingName, pageWidth / 2, y, { align: 'center' })
      } else {
        pdf.text(biz.tradingName, margin, y)
      }
      y += isThermal ? 5 : 8

      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(isThermal ? 7 : 11)
      if (biz.legalName && !isThermal) {
        pdf.text(biz.legalName, margin, y)
        y += 6
      }
      if (biz.address) {
        const addrLines = pdf.splitTextToSize(biz.address, contentWidth)
        addrLines.forEach((line) => {
          ensureSpace()
          pdf.text(line, isThermal ? pageWidth / 2 : margin, y, isThermal ? { align: 'center' } : undefined)
          y += isThermal ? 3.5 : 5
        })
      }
      const contactParts = []
      if (biz.phone) contactParts.push(biz.phone)
      if (biz.email) contactParts.push(biz.email)
      if (contactParts.length) {
        ensureSpace()
        const contact = contactParts.join(isThermal ? ' · ' : ' | ')
        const contactLines = pdf.splitTextToSize(contact, contentWidth)
        contactLines.forEach((line) => {
          pdf.text(line, isThermal ? pageWidth / 2 : margin, y, isThermal ? { align: 'center' } : undefined)
          y += isThermal ? 3.5 : 6
        })
      }

      y += isThermal ? 2 : 4
      dashedRule()

      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(headingSize)
      pdf.text('CASH-UP SLIP', isThermal ? pageWidth / 2 : margin, y, isThermal ? { align: 'center' } : undefined)
      y += isThermal ? 5 : 8

      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(bodySize)
      const createdAt = cashUpData.createdAt
        ? new Date(cashUpData.createdAt).toLocaleString('en-ZA')
        : new Date().toLocaleString('en-ZA')
      const metaLines = [`Date/time: ${createdAt}`]
      if (cashUpData.cashUpDate) metaLines.push(`POS date: ${cashUpData.cashUpDate}`)
      if (cashUpData.branchName || biz.branchName) {
        metaLines.push(`Branch: ${cashUpData.branchName || biz.branchName}`)
      }
      if (typeof cashUpData.transactionCount === 'number') {
        metaLines.push(`Transactions: ${cashUpData.transactionCount}`)
      }
      if (cashUpData.createdByName) metaLines.push(`Prepared by: ${cashUpData.createdByName}`)
      metaLines.forEach((line) => {
        ensureSpace()
        const wrapped = pdf.splitTextToSize(line, contentWidth)
        wrapped.forEach((w) => {
          pdf.text(w, margin, y)
          y += isThermal ? 3.8 : 6
        })
      })
      if (cashUpData.notes) {
        const notesLines = pdf.splitTextToSize(`Notes: ${cashUpData.notes}`, contentWidth)
        notesLines.forEach((line) => {
          ensureSpace()
          pdf.text(line, margin, y)
          y += isThermal ? 3.5 : 5
        })
      }

      y += isThermal ? 2 : 6
      dashedRule()

      const tillRows = [
        ['Cash', cashUpData.cashTotal ?? 0, cashUpData.countedCash],
        ['Card', cashUpData.cardTotal ?? 0, cashUpData.countedCard],
        ['EFT', cashUpData.eftTotal ?? 0, cashUpData.countedEft],
        ['Other', cashUpData.otherTotal ?? 0, cashUpData.countedOther],
      ]

      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(bodySize)
      pdf.text('Till check (POS vs counted)', margin, y)
      y += isThermal ? 5 : 8

      pdf.setFont('helvetica', 'normal')
      tillRows.forEach(([label, expected, countedRaw]) => {
        const countedVal = countedRaw != null && countedRaw !== '' ? Number(countedRaw) : Number(expected || 0)
        const diff = countedVal - Number(expected || 0)
        ensureSpace(isThermal ? 16 : 8)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(isThermal ? 8 : bodySize)
        pdf.text(String(label), labelX, y)
        y += isThermal ? 3.6 : 0
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(isThermal ? 7 : 9)
        if (isThermal) {
          pdf.text(`POS ${fmt(expected)}`, labelX, y)
          y += 3.4
          pdf.text(`Counted ${fmt(countedVal)}`, labelX, y)
          y += 3.4
          pdf.text(`Variance ${diff >= 0 ? '+' : '-'}${fmt(Math.abs(diff))}`, labelX, y)
          y += 4.2
        } else {
          pdf.text(`POS ${fmt(expected)}  |  Counted ${fmt(countedVal)}  |  Var ${diff >= 0 ? '+' : '-'}${fmt(Math.abs(diff))}`, valueX, y, { align: 'right' })
          y += lineGap + 1
        }
      })

      ensureSpace(12)
      pdf.setFontSize(isThermal ? 7 : 9)
      pdf.setTextColor(120, 100, 20)
      pdf.text('On account (not in till)', labelX, y)
      pdf.text(fmt(Number(cashUpData.accountTotal || 0)), valueX, y, { align: 'right' })
      y += isThermal ? 4 : 5
      pdf.setFontSize(isThermal ? 6.5 : 8)
      pdf.text(`Account txns: ${Number(cashUpData.accountTransactionCount || 0)}`, labelX, y)
      y += isThermal ? 5 : 8
      pdf.setTextColor(0, 0, 0)

      const tillGrandTotal = tillRows.reduce((sum, [, v]) => sum + Number(v || 0), 0)
      const countedGrand = tillRows.reduce((sum, [, expected, countedRaw]) => {
        const countedVal = countedRaw != null && countedRaw !== '' ? Number(countedRaw) : Number(expected || 0)
        return sum + countedVal
      }, 0)
      const grandDiff = countedGrand - tillGrandTotal
      ensureSpace(14)
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(isThermal ? 9 : 13)
      pdf.text('Grand total (POS)', labelX, y)
      pdf.text(fmt(tillGrandTotal), valueX, y, { align: 'right' })
      y += isThermal ? 5 : 7
      pdf.setFontSize(isThermal ? 8 : 11)
      pdf.text('Grand total (counted)', labelX, y)
      pdf.text(fmt(countedGrand), valueX, y, { align: 'right' })
      y += isThermal ? 5 : 7
      pdf.setFont('helvetica', 'normal')
      pdf.setFontSize(isThermal ? 8 : 10)
      pdf.text('Variance', labelX, y)
      pdf.text(`${grandDiff >= 0 ? '+' : '-'}${fmt(Math.abs(grandDiff))}`, valueX, y, { align: 'right' })

      y += isThermal ? 7 : 12
      pdf.setFont('helvetica', 'bold')
      pdf.setFontSize(bodySize)
      pdf.text('Items sold', labelX, y)
      y += isThermal ? 4 : 6

      const breakdown = cashUpBreakdownFromRecord(cashUpData)
      const breakdownSections = [
        ['Washes', breakdown.washes],
        ['Products', breakdown.products],
        ['Add-ons', breakdown.addons],
        ['Subscriptions', breakdown.subscriptions],
        ['Other', breakdown.otherLines],
      ]

      const drawBreakdownSection = (title, rows) => {
        if (!rows?.length) return
        const qtyCol = isThermal ? valueX - 22 : valueX - 38
        const amtCol = valueX
        const nameWidth = qtyCol - labelX - 2

        ensureSpace(isThermal ? 10 : 12)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(isThermal ? 7.5 : 10)
        pdf.text(title, labelX, y)
        y += isThermal ? 3.6 : 5

        if (!isThermal) {
          pdf.setFont('helvetica', 'normal')
          pdf.setFontSize(8)
          pdf.setTextColor(120, 120, 120)
          pdf.text('Item', labelX, y)
          pdf.text('Qty', qtyCol, y, { align: 'right' })
          pdf.text('Amount', amtCol, y, { align: 'right' })
          pdf.setTextColor(0, 0, 0)
          y += 4
        }

        let qtyTotal = 0
        let amtTotal = 0
        rows.forEach((r) => {
          const qty = Number(r.qty) || 0
          const amt = Number(r.amount) || 0
          qtyTotal += qty
          amtTotal += amt
          const nameLines = pdf.splitTextToSize(String(r.name || 'Item'), nameWidth)
          const rowH = isThermal ? 3.4 * nameLines.length + 3.2 : Math.max(5, nameLines.length * 4)
          ensureSpace(rowH + 2)
          pdf.setFont('helvetica', 'normal')
          pdf.setFontSize(isThermal ? 7 : 9)
          nameLines.forEach((line, i) => {
            pdf.text(line, labelX, y + i * (isThermal ? 3.4 : 4))
          })
          if (isThermal) {
            y += nameLines.length * 3.4
            pdf.setFontSize(6.5)
            pdf.text(`qty ${qty}   ${fmt(amt)}`, labelX, y)
            y += 3.8
          } else {
            pdf.text(String(qty), qtyCol, y, { align: 'right' })
            pdf.text(fmt(amt), amtCol, y, { align: 'right' })
            y += rowH
          }
        })

        ensureSpace(isThermal ? 6 : 7)
        pdf.setFont('helvetica', 'bold')
        pdf.setFontSize(isThermal ? 7 : 9)
        if (isThermal) {
          pdf.text(`Total  qty ${qtyTotal}   ${fmt(amtTotal)}`, labelX, y)
          y += 5
        } else {
          pdf.text('Total', labelX, y)
          pdf.text(String(qtyTotal), qtyCol, y, { align: 'right' })
          pdf.text(fmt(amtTotal), amtCol, y, { align: 'right' })
          y += 7
        }
      }

      const anyItems = breakdownSections.some(([, rows]) => rows?.length)
      if (!anyItems) {
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(isThermal ? 7 : 9)
        pdf.text('No item breakdown recorded.', labelX, y)
        y += isThermal ? 4 : 6
      } else {
        breakdownSections.forEach(([title, rows]) => drawBreakdownSection(title, rows))
      }

      if (biz.vatNumber || biz.registrationNumber) {
        y += isThermal ? 6 : 12
        ensureSpace(10)
        pdf.setFont('helvetica', 'normal')
        pdf.setFontSize(isThermal ? 6.5 : 10)
        const complianceParts = []
        if (biz.vatNumber) complianceParts.push(`VAT: ${biz.vatNumber}`)
        if (biz.registrationNumber) complianceParts.push(`Reg: ${biz.registrationNumber}`)
        const complianceLines = pdf.splitTextToSize(complianceParts.join(' | '), contentWidth)
        complianceLines.forEach((line) => {
          pdf.text(line, isThermal ? pageWidth / 2 : margin, y, isThermal ? { align: 'center' } : undefined)
          y += isThermal ? 3.5 : 8
        })
      }

      y += isThermal ? 6 : 10
      ensureSpace(12)
      this.drawThinkinCodeFooter(pdf, {
        pageWidth,
        y,
        lineHeight: isThermal ? 3.5 : 4.5,
        fontSize: isThermal ? 6 : 8,
      })

      return pdf.output('blob')
    } catch (error) {
      console.error('Error generating cash-up slip:', error)
      throw new Error('Failed to generate cash-up slip')
    }
  }
}

export default PDFGenerator
